import { useQuery } from "@tanstack/react-query";
import { courierAPI } from "../../utils/api";
import {
  Package,
  DollarSign,
  Clock,
  MapPin,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  AlertCircle,
  Star,
  Loader2,
  Calendar,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useMemo } from "react";

const formatMoney = (val) => {
  const num =
    typeof val === "string"
      ? parseFloat(val)
      : typeof val === "number"
        ? val
        : 0;
  return isNaN(num)
    ? "0.00"
    : num.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
};

const formatNumber = (val) => {
  const num =
    typeof val === "string"
      ? parseInt(val, 10)
      : typeof val === "number"
        ? val
        : 0;
  return isNaN(num) ? "0" : num.toLocaleString("en-US");
};

const StatCard = ({
  title,
  value,
  icon: Icon,
  color = "blue",
  trend,
  trendValue,
  isLoading,
}) => {
  const colors = {
    blue: "bg-blue-50 text-blue-600 border-blue-100",
    green: "bg-emerald-50 text-emerald-600 border-emerald-100",
    yellow: "bg-amber-50 text-amber-600 border-amber-100",
    purple: "bg-violet-50 text-violet-600 border-violet-100",
    cyan: "bg-cyan-50 text-cyan-600 border-cyan-100",
  };

  return (
    <div
      className={`card border ${colors[color]} hover:shadow-md transition-shadow duration-200`}
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-500 truncate">{title}</p>
          {isLoading ? (
            <div className="mt-2 h-8 flex items-center">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : (
            <p className="text-2xl font-bold text-gray-900 mt-2 truncate">
              {value}
            </p>
          )}
          {trend && !isLoading && (
            <div
              className={`flex items-center mt-2 text-sm font-medium ${
                trend === "up" ? "text-emerald-600" : "text-rose-600"
              }`}
            >
              {trend === "up" ? (
                <TrendingUp className="w-4 h-4 mr-1 flex-shrink-0" />
              ) : (
                <TrendingDown className="w-4 h-4 mr-1 flex-shrink-0" />
              )}
              <span className="truncate">{trendValue}</span>
            </div>
          )}
        </div>
        <div
          className={`p-3 rounded-xl flex-shrink-0 ${colors[color].replace("border-", "bg-").replace("50", "100")}`}
        >
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
};

const SkeletonCard = () => (
  <div className="card animate-pulse">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="h-4 bg-gray-200 rounded w-24"></div>
        <div className="h-8 bg-gray-200 rounded w-16 mt-3"></div>
        <div className="h-4 bg-gray-200 rounded w-20 mt-2"></div>
      </div>
      <div className="h-12 w-12 bg-gray-200 rounded-xl"></div>
    </div>
  </div>
);

const EmptyChart = ({ title }) => (
  <div className="card flex flex-col items-center justify-center h-[250px]">
    <Calendar className="w-12 h-12 text-gray-300 mb-3" />
    <p className="text-gray-500 font-medium">{title}</p>
    <p className="text-sm text-gray-400 mt-1">No data available</p>
  </div>
);

const CourierDashboard = () => {
  const {
    data: statsData,
    isLoading: statsLoading,
    error: statsError,
  } = useQuery({
    queryKey: ["courierStats"],
    queryFn: () => courierAPI.dashboard(),
    refetchInterval: 30000,
    retry: 2,
  });

  // Use courier-specific endpoints (NOT dashboardAPI)
  const { data: chartData, isLoading: chartLoading } = useQuery({
    queryKey: ["courierChart"],
    queryFn: () => courierAPI.chartData(7),
    retry: 2,
  });

  const { data: activityData, isLoading: activityLoading } = useQuery({
    queryKey: ["courierActivity"],
    queryFn: () => courierAPI.recentActivity(5),
    retry: 2,
  });

  const stats = useMemo(() => statsData?.data?.data || {}, [statsData]);
  const chart = useMemo(() => chartData?.data?.data || {}, [chartData]);
  const activity = useMemo(
    () => activityData?.data?.data || [],
    [activityData],
  );

  if (statsError) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-rose-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900">
            Failed to load dashboard
          </h3>
          <p className="text-gray-500 mt-2">Please try refreshing the page</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Courier Dashboard
          </h1>
          <p className="text-gray-500 mt-1">
            Your delivery overview and performance
          </p>
        </div>
        <div className="text-sm text-gray-400">Auto-refreshes every 30s</div>
      </div>

      {/* Primary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statsLoading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : (
          <>
            <StatCard
              title="Pending Pickups"
              value={formatNumber(stats.pending_pickups)}
              icon={Package}
              color="yellow"
            />
            <StatCard
              title="In Transit"
              value={formatNumber(stats.in_transit)}
              icon={MapPin}
              color="blue"
            />
            <StatCard
              title="Today's Deliveries"
              value={formatNumber(stats.today_deliveries)}
              icon={CheckCircle}
              color="green"
            />
            <StatCard
              title="Today's Earnings"
              value={`EGP ${formatMoney(stats.today_earnings)}`}
              icon={DollarSign}
              color="purple"
              trend="up"
              trendValue={`+EGP ${formatMoney(stats.today_earnings_vs_yesterday || 0)} vs yesterday`}
            />
          </>
        )}
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {statsLoading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : (
          <>
            <StatCard
              title="Weekly Earnings"
              value={`EGP ${formatMoney(stats.weekly_earnings)}`}
              icon={TrendingUp}
              color="green"
            />
            <StatCard
              title="Total Deliveries"
              value={formatNumber(stats.total_deliveries)}
              icon={Package}
              color="blue"
            />
            <StatCard
              title="Rating"
              value={`${stats.rating || 0}/5`}
              icon={Star}
              color="yellow"
            />
          </>
        )}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Package className="w-5 h-5 mr-2 text-blue-500" />
            Deliveries (Last 7 Days)
          </h3>
          {chartLoading ? (
            <div className="h-[250px] flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : chart.deliveries_per_day &&
            chart.deliveries_per_day.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={chart.deliveries_per_day}>
                <defs>
                  <linearGradient
                    id="deliveriesGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12, fill: "#6b7280" }}
                  tickLine={false}
                  axisLine={{ stroke: "#e5e7eb" }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: "#6b7280" }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: "8px",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  fill="url(#deliveriesGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart title="No delivery data available" />
          )}
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <DollarSign className="w-5 h-5 mr-2 text-emerald-500" />
            Earnings (Last 7 Days)
          </h3>
          {chartLoading ? (
            <div className="h-[250px] flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : chart.earnings_per_day && chart.earnings_per_day.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={chart.earnings_per_day}>
                <defs>
                  <linearGradient
                    id="earningsGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12, fill: "#6b7280" }}
                  tickLine={false}
                  axisLine={{ stroke: "#e5e7eb" }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: "#6b7280" }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `$${v}`}
                />
                <Tooltip
                  formatter={(value) => [`$${value}`, "Earnings"]}
                  contentStyle={{
                    borderRadius: "8px",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="earnings"
                  stroke="#10b981"
                  strokeWidth={2}
                  fill="url(#earningsGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart title="No earnings data available" />
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Clock className="w-5 h-5 mr-2 text-blue-500" />
          Recent Activity
        </h3>
        {activityLoading ? (
          <div className="h-[200px] flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : activity.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <Calendar className="w-12 h-12 mb-3 text-gray-300" />
            <p className="font-medium">No recent activity</p>
            <p className="text-sm mt-1">Activities will appear here</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
            {activity.map((item, index) => (
              <div
                key={index}
                className="flex items-start space-x-3 p-3 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100"
              >
                <div
                  className={`p-2.5 rounded-lg flex-shrink-0 ${
                    item.status === "delivered"
                      ? "bg-emerald-100 text-emerald-600"
                      : item.status === "failed"
                        ? "bg-rose-100 text-rose-600"
                        : "bg-blue-100 text-blue-600"
                  }`}
                >
                  {item.status === "delivered" ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : item.status === "failed" ? (
                    <AlertCircle className="w-4 h-4" />
                  ) : (
                    <Clock className="w-4 h-4" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {item.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">{item.time}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CourierDashboard;
